/**
 * @bos/portal3 — the VERDICT screen (batch 8): the end of the run, opened from the rail's
 * CHECKS row. It answers ONE question — "what did I get, and can I trust it?" — from the
 * composed verdict the server shaped (verdict-artifacts.ts), and it is built around the
 * honesty block: a machine that lists its own blind spots is one you can believe about
 * everything else, so WHAT COULD NOT BE PROVEN gets real weight, on the green screen too.
 *
 * WORDING LAWS:
 *   - A not-ok verdict is NOT a failure screen with an apology: it is the machine
 *     refusing to call the build done, and the person is told exactly which part they
 *     can rely on (the reasons render in the machine's own sentences, verbatim).
 *   - A green verdict is NOT dressed up: the headline claims exactly "every check that
 *     ran came back good", and the same screen says what never ran, what was skipped,
 *     what was waived, and what is implemented on trust. One real run was green with
 *     its whole spec comparison waived — a screen showing only the green would lie.
 *   - A REDUCED app leads. When parts were left out, that is the first thing under the
 *     headline, with the staging policy's own reason per part — the checks below then
 *     read as statements about what shipped, not about the app that was asked for.
 *   - An UNPROVEN (attested) rule is NEVER rendered as a pass: it appears only in the
 *     could-not-prove section and on its module's "on trust" line.
 *   - Nothing is invented: no score, no grade, no confidence, no percentage. Every
 *     number is a count the server read out of the artifacts.
 *   - Check/gate ids are internal vocabulary; the screen renders owner labels (the
 *     §9.11 discipline) and keeps the machine's own summary sentence beside them.
 *
 * READ-ONLY BY DESIGN: there is no approval door here and none is faked — the person's
 * doors (the data model, the sign-off) are earlier in the run.
 */
import type { VerdictCheckDoc, VerdictDoc, VerdictGateDoc, VerdictModuleDoc } from '@bos/portal3-server';
import type { StageRead, StageReviewStatus } from './hooks';
import type { RailState } from './model';

// ── owner labels (client-side words for internal ids; unknown ids stay verbatim) ─────

const CHECK_LABELS = new Map<string, string>([
  ['BOOT', 'It starts and answers'],
  ['AUTH', 'Sign-in is enforced; the wrong role is refused'],
  ['API_SURFACE', 'Every operation answers at its door'],
  ['SCREEN_DATA', 'Every screen gets its data'],
  ['JOURNEY_REPLAY', 'The workflows run end to end'],
  ['JOURNEY_REPLAY_ACTOR', 'The workflows run as the people who will use them'],
  ['CERTIFY_INVARIANTS', 'The bookkeeping held together'],
  ['EVENT_SEAMS', 'Parts react to each other’s events'],
  ['SCHEDULES', 'Timed work fires on its own'],
  ['RESTART_PERSISTENCE', 'Nothing is lost across a restart'],
]);

const GATE_LABELS = new Map<string, string>([
  ['compile', 'The code compiles as an app'],
  ['spa-build', 'The user interface builds'],
  ['spa-typecheck', 'The screens compile exactly as shipped'],
  ['wire-contract', 'The screens send exactly what the server accepts'],
  ['input-validation', 'The server refuses bad input at its door'],
  ['unfilled-holes', 'Nothing was left half-written'],
  ['surface-coherence', 'Every screen is reachable and fed'],
]);

export function checkLabel(id: string): string {
  return CHECK_LABELS.get(id) ?? id;
}

export function gateLabel(key: string): string {
  return GATE_LABELS.get(key) ?? key;
}

// ── the honest non-ready sentences ───────────────────────────────────────────────────

/** The absent sentence: not-yet vs ended-without. `terminal` = the run is over, so the
 *  absence is final for this arc (a resume could still compose one). */
export function verdictAbsentSentence(terminal: boolean): string {
  return terminal
    ? 'This run ended before the machine composed its final verdict — nothing was rolled up, ' +
        'so nothing is claimed here. The Journal tells the story of where it stopped; ' +
        'resuming the run picks up from its last checkpoint.'
    : 'The machine hasn’t reached its final verdict yet — it is composed at the very end of the ' +
        'run, after every check has run. This screen fills in the moment it lands.';
}

export const VERDICT_UNINTELLIGIBLE_SENTENCE =
  'The run wrote a verdict this screen can’t read — the file is there, but not in a shape this ' +
  'portal understands. If the machine is still working, it may be mid-write; check again shortly.';

// ── small pieces ─────────────────────────────────────────────────────────────────────

function ReportOnlyNote() {
  return (
    <p className="sr__readonly" role="note">
      There is nothing to approve here — this is the machine’s report of what it built and what it
      checked. Your doors (the data model, the sign-off) were earlier in the run.
    </p>
  );
}

function SectionHead({ title }: { title: string }) {
  return <h3 className="sr__section">{title}</h3>;
}

/** One check/gate result row. `state` picks the mark; n/a and not-run are NEVER passes
 *  and read as their own words. */
function ResultRow({ label, state, detail }: { label: string; state: string; detail: string | null }) {
  const mark = state === 'pass' ? '✓' : state === 'fail' ? '✗' : '—';
  const stateWord =
    state === 'pass' ? null : state === 'fail' ? 'failed' : state === 'n/a' ? 'nothing to check' : 'never ran';
  return (
    <li className={`vr-row vr-row--${state === 'n/a' ? 'na' : state}`}>
      <span className="vr-row__mark" aria-hidden="true">
        {mark}
      </span>
      <span className="vr-row__label">
        {label}
        {stateWord !== null && <span className="vr-row__state"> — {stateWord}</span>}
      </span>
      {detail !== null && detail !== '' && <span className="vr-row__detail">{detail}</span>}
    </li>
  );
}

function checkState(c: VerdictCheckDoc): string {
  if (c.status === 'pass') return 'pass';
  if (c.status === 'fail' || c.status === 'error') return 'fail';
  if (c.status === 'n/a' || c.status === 'skipped') return 'n/a';
  return c.status; // an unknown status word renders verbatim, never coerced to a pass
}

// ── the sections ─────────────────────────────────────────────────────────────────────

function Headline({ doc }: { doc: VerdictDoc }) {
  if (doc.ok) {
    return (
      <>
        <p className="vr-headline vr-headline--ok">
          {doc.appName !== null ? `${doc.appName} is built` : 'Your app is built'}, and every check
          the machine ran came back good.
        </p>
        <p className="sr__lede">
          That sentence is exactly as strong as what was checked — and the machine keeps a list of
          what it could <strong>not</strong> check. Read “What the machine could not prove” below
          before you rely on this.
        </p>
      </>
    );
  }
  return (
    <>
      <p className="vr-headline vr-headline--not-ok">
        The machine refuses to call this build done.
      </p>
      <p className="sr__lede">
        That is not a crash — it is the final check telling the truth about a partial result. What
        stands is listed below exactly as far as it stands; what does not is named here, in the
        machine’s own words:
      </p>
      <ul className="vr-reasons" aria-label="Why it is not OK">
        {doc.reasons.map((r, i) => (
          <li key={i} className="vr-reason">
            {r}
          </li>
        ))}
      </ul>
    </>
  );
}

function ModuleRow({ m }: { m: VerdictModuleDoc }) {
  return (
    <li className={`vr-module${m.certified ? '' : ' vr-module--failed'}`} aria-label={m.slug}>
      <span className="vr-module__name">
        {m.slug}
        {m.certified ? (
          m.certifiedBy === 'owner-tolerance' ? (
            <span className="sr-chip">certified by your waiver</span>
          ) : (
            <span className="sr-chip">certified</span>
          )
        ) : (
          <span className="sr-chip vr-chip--danger">not certified</span>
        )}
      </span>
      <span className="sr__meta">
        {m.checksPassed} check{m.checksPassed === 1 ? '' : 's'} passed against it
        {m.unproven.length > 0 &&
          ` · ${m.unproven.length} rule${m.unproven.length === 1 ? '' : 's'} implemented on trust — see below`}
      </span>
      {m.certifiedBy === 'owner-tolerance' && (
        <span className="vr-module__tolerance">
          The oracle did not certify this part — you accepted its known defects at the sign-off
          {m.toleratedBy.length > 0 && ` (your answer ${m.toleratedBy.join(', ')})`}. That decision,
          not a passing check, is what ships it.
        </span>
      )}
      {m.reasonsUncertified.map((r, i) => (
        <span key={`r${i}`} className="vr-module__failure">
          {r}
        </span>
      ))}
      {m.failures.map((f, i) => (
        <span key={`f${i}`} className="vr-module__failure">
          {f}
        </span>
      ))}
    </li>
  );
}

function BuiltSection({ doc }: { doc: VerdictDoc }) {
  const reduced = doc.gradedApp === 'reduced' || doc.excluded.length > 0;
  return (
    <>
      <SectionHead title="What you got" />
      {reduced ? (
        <div className="vr-reduced" role="alert">
          <p className="vr-reduced__head">
            This is a reduced app: {doc.excluded.length} of {doc.declaredCount} part
            {doc.declaredCount === 1 ? '' : 's'} of your app {doc.excluded.length === 1 ? 'was' : 'were'} left
            out.
          </p>
          <ul className="vr-reduced__list">
            {doc.excluded.map((e) => (
              <li key={e.slug}>
                <strong>{e.slug}</strong> — {e.reason}
              </li>
            ))}
          </ul>
          <p className="vr-reduced__note">
            Every check below is a statement about what shipped — not about the app you asked for.
          </p>
        </div>
      ) : (
        doc.stagedCount !== null && (
          <p className="sr__lede">
            Everything that was declared shipped: {doc.stagedCount} part{doc.stagedCount === 1 ? '' : 's'}.
          </p>
        )
      )}
      <ul className="vr-modules">
        {doc.modules.map((m) => (
          <ModuleRow key={m.slug} m={m} />
        ))}
      </ul>
    </>
  );
}

function CheckedSection({ doc }: { doc: VerdictDoc }) {
  const t = doc.certifyTotals;
  return (
    <>
      <SectionHead title="What was checked" />
      {t !== null && (
        <p className="sr__lede">
          The finished app was started and driven for real: {t.passed} of {t.checks} checks passed
          {t.failed > 0 && `, ${t.failed} failed`}
          {t.na > 0 && `, ${t.na} had nothing to apply to`}
          {t.unverified > 0 && `, ${t.unverified} could not be verified`}.
        </p>
      )}
      {doc.verifiedBeyondBoot === false && (
        <p className="vr-vacuous" role="alert">
          Only liveness was verified — no check beyond “it boots” passed. A certification like that
          says nothing about how the app behaves.
        </p>
      )}
      {doc.checks.length > 0 && (
        <ul className="vr-rows" aria-label="Checks against the running app">
          {doc.checks.map((c) => (
            <ResultRow key={c.id} label={checkLabel(c.id)} state={checkState(c)} detail={c.summary} />
          ))}
        </ul>
      )}
      <ul className="vr-rows" aria-label="Checks against the shipped code">
        {doc.gates.map((g: VerdictGateDoc) => (
          <ResultRow key={g.key} label={gateLabel(g.key)} state={g.state} detail={g.detail} />
        ))}
        {doc.screens !== null &&
          (doc.screens.generated ? (
            <ResultRow
              label="Every screen renders"
              state={doc.screens.failed > 0 || doc.screens.roleLeaks > 0 ? 'fail' : 'pass'}
              detail={
                `${doc.screens.green} of ${doc.screens.total} screen(s) green` +
                (doc.screens.roleLeaks > 0
                  ? `, ${doc.screens.roleLeaks} role leak(s) — a screen shows itself to a role it should not`
                  : ', no role sees a screen it shouldn’t')
              }
            />
          ) : (
            <ResultRow label="Every screen renders" state="n/a" detail={doc.screens.skippedReason ?? 'the screen step was skipped'} />
          ))}
      </ul>
    </>
  );
}

function NotProvenSection({ doc }: { doc: VerdictDoc }) {
  const naChecks = doc.checks.filter((c) => checkState(c) === 'n/a');
  const naGates = doc.gates.filter((g) => g.state === 'n/a' || g.state === 'not-run');
  const sf = doc.specFidelity;
  const specNotCompared = sf !== null && !sf.computable;
  const specWaivedClaims = sf !== null && sf.computable && sf.ownerWaived > 0;
  const signoffMachine = doc.signoff !== null && doc.signoff.kind === 'machine';
  const signoffWaived = doc.signoff !== null && doc.signoff.kind === 'waived';
  const anything =
    doc.unprovenTotal > 0 ||
    naChecks.length > 0 ||
    naGates.length > 0 ||
    doc.notChecked.length > 0 ||
    specNotCompared ||
    specWaivedClaims ||
    signoffMachine ||
    signoffWaived;

  return (
    <section className="vr-honesty" aria-label="What the machine could not prove">
      <h3 className="vr-honesty__title">What the machine could not prove</h3>
      <p className="vr-honesty__lede">
        A machine that lists its own blind spots is one you can believe about everything else.
        Nothing here failed — these are the places where “no problem found” means “not looked at”,
        and where your trust, not a passing check, is what carries the weight.
      </p>
      {!anything && (
        <p className="page__note">
          Nothing was held back on this run: every declared rule was machine-exercised, every check
          had something to check, and your brief was compared against what shipped.
        </p>
      )}

      {doc.unprovenTotal > 0 && (
        <>
          <h4 className="vr-honesty__sub">
            Rules written into the code, but never proven ({doc.unprovenTotal})
          </h4>
          <p className="sr__lede">
            The machine could not construct a test that would catch these failing, so their reject
            side was never exercised. A handler that quietly ignores such a rule would look exactly
            the same. They ship as your word, not the machine’s.
          </p>
          <ul className="sr-list">
            {doc.modules
              .filter((m) => m.unproven.length > 0)
              .map((m) =>
                m.unproven.map((rule, i) => (
                  <li key={`${m.slug}-${i}`} className="sr-list__row">
                    <span className="sr-list__what">{m.slug}</span>
                    <span className="sr-list__reason">{rule}</span>
                  </li>
                )),
              )}
          </ul>
        </>
      )}

      {specNotCompared && (
        <>
          <h4 className="vr-honesty__sub">Your brief was never compared against the shipped app</h4>
          <p className="sr__lede">
            No check ever asked “is this the app that was asked for?” —{' '}
            {sf.notComputableReason ?? 'the comparison could not be computed'}.
            {sf.waived && ' That comparison was waived, not performed — the green above does not include it.'}
          </p>
        </>
      )}
      {specWaivedClaims && (
        <>
          <h4 className="vr-honesty__sub">
            Things you asked for that were waived rather than built ({sf.ownerWaived} of {sf.claimsTotal})
          </h4>
          <p className="sr__lede">
            You waived these at the sign-off — they are absent from the app by your decision, and no
            check covers them.
          </p>
          {sf.absences.length > 0 && (
            <ul className="sr-list">
              {sf.absences.map((a, i) => (
                <li key={i} className="sr-list__row">
                  <span className="sr-list__reason">{a}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {doc.notChecked.length > 0 && (
        <>
          <h4 className="vr-honesty__sub">Operations the certification deliberately skipped</h4>
          <ul className="sr-list">
            {doc.notChecked.map((s) => (
              <li key={s.fqn} className="sr-list__row">
                <span className="sr-list__what">{s.fqn}</span>
                <span className="sr-list__reason">{s.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {naChecks.length > 0 && (
        <>
          <h4 className="vr-honesty__sub">Checks that had nothing to apply to</h4>
          <ul className="sr-list">
            {naChecks.map((c) => (
              <li key={c.id} className="sr-list__row">
                <span className="sr-list__what">{checkLabel(c.id)}</span>
                {c.summary !== null && <span className="sr-list__reason">{c.summary}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {naGates.length > 0 && (
        <>
          <h4 className="vr-honesty__sub">Gates that did not grade this build</h4>
          <ul className="sr-list">
            {naGates.map((g) => (
              <li key={g.key} className="sr-list__row">
                <span className="sr-list__what">{gateLabel(g.key)}</span>
                <span className="sr-list__reason">
                  {g.state === 'not-run'
                    ? 'never ran — the verdict treats a missing gate as failing, never as passing'
                    : (g.detail ?? 'not applicable to this build')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {signoffMachine && (
        <p className="vr-module__failure">
          No human signed off on the brief — the machine approved it itself. “Approved” here is a
          claim nobody stands behind.
        </p>
      )}
      {signoffWaived && doc.signoff !== null && (
        <p className="sr__lede">
          The sign-off review was waived by {doc.signoff.actor ?? 'an unnamed actor'} — no findings
          were reviewed before the build proceeded.
        </p>
      )}
    </section>
  );
}

function RulesSection({ doc }: { doc: VerdictDoc }) {
  const rules = doc.rules;
  if (rules === null) return null; // the census never ran — its fail-closed reason is in the headline block
  const hasProblem = rules.droppedCount > 0 || rules.unenforced.length > 0;
  if (!hasProblem) {
    return (
      <>
        <SectionHead title="Rules" />
        <p className="sr__lede">
          No rule was dropped between your approved model and the shipped app
          {rules.dropped.length > 0 && ' — and where a rule is enforced through another door, that is on the record:'}
        </p>
        {rules.dropped.length > 0 && (
          <ul className="sr-list">
            {rules.dropped.map((r, i) => (
              <li key={i} className="sr-list__row">
                <span className="sr-list__reason">{r}</span>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  return (
    <section className="vr-dropped" aria-label="Rules your app does not enforce">
      <h3 className="vr-dropped__title">Rules your app does not enforce</h3>
      <p className="sr__lede">
        These rules are part of the model you approved — and the shipped app does not hold them.
        Anything that depends on one of them is unprotected.
      </p>
      <ul className="sr-list">
        {rules.unenforced.map((u, i) => (
          <li key={i} className="sr-list__row">
            <span className="sr-list__what">{u.module}</span>
            <span className="sr-list__reason">
              {u.rule ?? 'the emitted guard carries no rule text'}
              {u.mis && ' — worse: this guard runs, but enforces something other than this rule'}
              {u.why !== null && <span className="sr__meta"> ({u.why})</span>}
            </span>
          </li>
        ))}
        {rules.dropped.map((r, i) => (
          <li key={`d${i}`} className="sr-list__row">
            <span className="sr-list__reason">{r}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SignoffLine({ doc }: { doc: VerdictDoc }) {
  const s = doc.signoff;
  if (s === null || s.kind !== 'human') return null; // machine/waived live in the honesty block
  return (
    <p className="sr__meta vr-signoff">
      Signed off by {s.actor ?? 'a person'}
      {s.findingsPresented !== null &&
        `, over ${s.findingsPresented} finding${s.findingsPresented === 1 ? '' : 's'} presented`}
      {doc.composedAt !== null && ` · verdict composed ${doc.composedAt}`}
    </p>
  );
}

// ── the section itself ───────────────────────────────────────────────────────────────

export interface VerdictReviewProps {
  readonly payload: StageRead<VerdictDoc> | null;
  readonly status: StageReviewStatus;
  readonly error: string | null;
  /** Where the rail says the checks row is — unused for wording beyond honesty of the
   *  absent state, which keys off `terminal` (the run's own status). */
  readonly railState: RailState | null;
  /** Is the run over? Picks the honest absent sentence: not-yet vs ended-without. */
  readonly terminal: boolean;
}

export function VerdictReview({ payload, status, error, terminal }: VerdictReviewProps) {
  return (
    <section className="sr" aria-label="Checks">
      <div className="sr__head">
        <h2 className="sr__title">The verdict</h2>
      </div>
      <ReportOnlyNote />
      {status === 'loading' && <p className="page__note">Reading the machine’s verdict…</p>}
      {status === 'error' && (
        <p className="form__error" role="alert">
          {error ?? 'Something went wrong reading the verdict.'}
        </p>
      )}
      {status === 'ready' && payload !== null && payload.state === 'absent' && (
        <p className="page__note">{verdictAbsentSentence(terminal)}</p>
      )}
      {status === 'ready' && payload !== null && payload.state === 'unintelligible' && (
        <p className="page__note">{VERDICT_UNINTELLIGIBLE_SENTENCE}</p>
      )}
      {status === 'ready' && payload !== null && payload.state === 'ready' && payload.doc !== null && (
        <>
          <Headline doc={payload.doc} />
          <BuiltSection doc={payload.doc} />
          <CheckedSection doc={payload.doc} />
          <NotProvenSection doc={payload.doc} />
          <RulesSection doc={payload.doc} />
          <SignoffLine doc={payload.doc} />
        </>
      )}
    </section>
  );
}
